import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { projets as apiProjets, taches as apiTaches, messageErreur } from '../services/api';
import { PRIORITES, STATUTS_PROJET, STATUTS_TACHE, libelle } from '../constants';
import Kanban from '../components/Kanban';
import FicheTache from '../components/FicheTache';
import { ModaleNouvelleTache, ModaleProjet } from '../components/Formulaires';
import {
  Avancement, Avatar, BadgeEntreprise, BadgePriorite, Chargement, Erreur, Modale, useReferentiels,
  IconeCalendrier, IconeMessage,
} from '../components/communs';
import { dateCourte, enRetard, nomComplet } from '../utils/format';

function CarteTache({ t }) {
  return (
    <>
      <div className="kanban-titre">{t.titre}</div>
      <div className="carte-ligne">
        <span className="carte-meta">
          <BadgePriorite priorite={t.priorite} />
          {t.date_echeance && <span className={enRetard(t.date_echeance, t.statut) ? 'retard' : ''}><IconeCalendrier /> {dateCourte(t.date_echeance)}</span>}
          {t.nb_messages > 0 && <span title={`${t.nb_messages} message(s)`}><IconeMessage /> {t.nb_messages}</span>}
        </span>
        <Avatar prenom={t.assigne_prenom} nom={t.assigne_nom} titre={t.assigne_id ? `Assignée à ${nomComplet(t, 'assigne_')}` : 'Non assignée'} />
      </div>
    </>
  );
}

// Suppression d'un projet : on nomme les autres personnes qui le perdront et on
// propose d'archiver à la place. Si le projet concerne quelqu'un d'autre, la
// suppression demande une case cochée : une simple confirmation du navigateur
// a déjà fait partir des projets partagés avec ceux de leur auteur.
function ModaleSuppression({ d, moi, onFermer, onArchiver, onSupprimer }) {
  const { projet: p, membres, taches } = d;
  const autres = useMemo(() => {
    const parId = new Map();
    const ajouter = (id, prenom, nom) => { if (id && id !== moi.id && !parId.has(id)) parId.set(id, { id, prenom, nom }); };
    ajouter(p.responsable_id, p.responsable_prenom, p.responsable_nom);
    for (const m of membres) ajouter(m.id, m.prenom, m.nom);
    for (const t of taches) ajouter(t.assigne_id, t.assigne_prenom, t.assigne_nom);
    return [...parId.values()].map((x) => ({
      ...x,
      role: x.id === p.responsable_id ? 'responsable' : 'membre',
      nbTaches: taches.filter((t) => t.assigne_id === x.id).length,
    }));
  }, [p, membres, taches, moi.id]);
  const [compris, setCompris] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const agir = (f) => async () => {
    setEnvoi(true);
    try { await f(); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  const partage = autres.length > 0;

  return (
    <Modale className="modale-suppression" titre={`Supprimer « ${p.nom} » ?`} onFermer={onFermer} actions={(
      <>
        <button type="button" className="btn" onClick={onFermer} disabled={envoi}>Annuler</button>
        {!p.archive && <button type="button" className="btn btn-primaire" onClick={agir(onArchiver)} disabled={envoi}>Archiver plutôt</button>}
        <button type="button" className="btn btn-danger" onClick={agir(onSupprimer)} disabled={envoi || (partage && !compris)}>Supprimer définitivement</button>
      </>
    )}>
      {erreur && <Erreur>{erreur}</Erreur>}
      <p style={{ marginTop: 0 }}>
        Le projet, ses {taches.length} tâche{taches.length > 1 ? 's' : ''}, leurs murs d’activité et leurs pièces jointes seront effacés.
        On ne peut pas revenir en arrière. Un projet archivé, lui, disparaît du kanban mais se retrouve dans les archives.
      </p>
      {partage ? (
        <>
          <div className="note-bandeau">
            Ce projet ne vous concerne pas seul : {autres.length > 1 ? `${autres.length} autres personnes le perdront` : 'une autre personne le perdra'} aussi.
          </div>
          <div className="liste-simple" style={{ marginBottom: 'var(--esp-3)' }}>
            {autres.map((x) => (
              <div key={x.id} className="liste-ligne">
                <Avatar prenom={x.prenom} nom={x.nom} />
                <span style={{ flex: 1 }}>{nomComplet(x)}</span>
                <span className="faible">
                  {x.role}{x.nbTaches ? ` · ${x.nbTaches} tâche${x.nbTaches > 1 ? 's' : ''} assignée${x.nbTaches > 1 ? 's' : ''}` : ''}
                </span>
              </div>
            ))}
          </div>
          <label className="bascule">
            <input type="checkbox" checked={compris} onChange={(e) => setCompris(e.target.checked)} />
            Je supprime aussi ce projet pour {autres.length > 1 ? 'ces personnes' : 'cette personne'}
          </label>
        </>
      ) : <p className="faible">Personne d’autre que vous n’est rattaché à ce projet.</p>}
    </Modale>
  );
}

export default function Projet({ moi }) {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const naviguer = useNavigate();
  const ref = useReferentiels();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [edition, setEdition] = useState(false);
  const [suppression, setSuppression] = useState(false);
  const [nouvelle, setNouvelle] = useState(null); // statut de la colonne où créer
  const [filtreAssigne, setFiltreAssigne] = useState(null);
  const [filtrePriorite, setFiltrePriorite] = useState('');
  const tacheOuverte = params.get('tache') ? Number(params.get('tache')) : null;

  const charger = useCallback(() => apiProjets.detail(id)
    .then((r) => { setD(r); setErreur(null); })
    .catch((e) => setErreur(messageErreur(e))), [id]);
  useEffect(() => { setD(null); charger(); }, [charger]);

  const ouvrirTache = (tid) => {
    const p = new URLSearchParams(params);
    if (tid) p.set('tache', tid); else p.delete('tache');
    setParams(p, { replace: !tid });
  };

  const tachesFiltrees = useMemo(() => (d?.taches || []).filter((t) => (
    (!filtreAssigne || (filtreAssigne === -1 ? !t.assigne_id : t.assigne_id === filtreAssigne))
    && (!filtrePriorite || t.priorite === filtrePriorite)
  )), [d, filtreAssigne, filtrePriorite]);

  async function deplacer(t, statut, position) {
    setD((x) => {
      const autres = x.taches.filter((y) => y.id !== t.id);
      const colonne = autres.filter((y) => y.statut === statut).sort((a, b) => a.position - b.position);
      colonne.splice(position, 0, { ...t, statut });
      const rangs = new Map(colonne.map((y, i) => [y.id, i]));
      return { ...x, taches: [...autres.filter((y) => y.statut !== statut), ...colonne].map((y) => (rangs.has(y.id) ? { ...y, statut, position: rangs.get(y.id) } : y)) };
    });
    try {
      await apiTaches.deplacer(t.id, statut, position);
      charger();
    } catch (e) {
      setErreur(messageErreur(e));
      charger();
    }
  }

  async function supprimerProjet() {
    await apiProjets.supprimer(d.projet.id);
    naviguer('/projets');
  }

  async function archiverProjet() {
    const x = d.projet;
    await apiProjets.modifier(x.id, {
      nom: x.nom, description: x.description, entreprise_id: x.entreprise_id, responsable_id: x.responsable_id,
      statut: x.statut, date_debut: x.date_debut, date_echeance: x.date_echeance, archive: true,
    });
    naviguer('/projets');
  }

  if (erreur && !d) {
    return (
      <div className="page-container">
        <div className="fil-ariane"><Link to="/projets">← Projets</Link></div>
        <Erreur onReessayer={charger}>{erreur}</Erreur>
      </div>
    );
  }
  if (!d) return <div className="page-container"><Chargement /></div>;
  const { projet: p, membres } = d;

  return (
    <div className="page-container">
      <div className="fil-ariane"><Link to="/projets">← Projets</Link></div>
      <div className="projet-entete">
        <div>
          <h1 style={{ margin: 0, fontSize: 21, fontWeight: 650 }}>{p.nom}</h1>
          <div className="projet-infos">
            <span className="badge badge-neutre badge-sans-point">{libelle(STATUTS_PROJET, p.statut)}</span>
            {p.archive && <span className="badge badge-alerte badge-sans-point">Archivé</span>}
            {p.entreprise_nom && <BadgeEntreprise nom={p.entreprise_nom} type={p.entreprise_type} />}
            <span>Responsable : <b>{nomComplet(p, 'responsable_') || '—'}</b></span>
            {p.date_debut && <span>Début : <b>{dateCourte(p.date_debut)}</b></span>}
            {p.date_echeance && <span>Échéance : <b className={enRetard(p.date_echeance, p.statut) ? 'retard' : ''}>{dateCourte(p.date_echeance)}</b></span>}
            <span className="membres" title={membres.map((m) => nomComplet(m)).join(', ')}>
              {membres.slice(0, 8).map((m) => <Avatar key={m.id} prenom={m.prenom} nom={m.nom} />)}
              {membres.length > 8 && <span className="faible" style={{ marginLeft: 4 }}>+{membres.length - 8}</span>}
            </span>
          </div>
          {p.description && <p className="projet-description">{p.description}</p>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
          <Avancement fait={p.nb_terminees} total={p.nb_taches} />
          <div className="page-header-actions" style={{ justifyContent: 'flex-end' }}>
            {d.peut_gerer && <button type="button" className="btn" onClick={() => setEdition(true)}>Modifier</button>}
            {d.peut_gerer && <button type="button" className="btn btn-danger" onClick={() => setSuppression(true)}>Supprimer</button>}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div style={{ minWidth: 190 }}>
          <select aria-label="Filtrer par personne" value={filtreAssigne || ''} onChange={(e) => setFiltreAssigne(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Toutes les personnes</option>
            <option value={moi.id}>Mes tâches</option>
            <option value={-1}>Non assignées</option>
            {ref.utilisateurs.filter((u) => u.id !== moi.id).map((u) => <option key={u.id} value={u.id}>{nomComplet(u)}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 170 }}>
          <select aria-label="Filtrer par priorité" value={filtrePriorite} onChange={(e) => setFiltrePriorite(e.target.value)}>
            <option value="">Toutes les priorités</option>
            {PRIORITES.map((x) => <option key={x.cle} value={x.cle}>Priorité {x.libelle.toLowerCase()}</option>)}
          </select>
        </div>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-primaire" onClick={() => setNouvelle('a_faire')}>Nouvelle tâche</button>
      </div>

      {erreur && <Erreur onReessayer={charger}>{erreur}</Erreur>}
      {d.taches.length === 0 ? (
        <div className="vide carte">
          Aucune tâche dans ce projet.
          <button type="button" className="btn btn-primaire" style={{ marginLeft: 8 }} onClick={() => setNouvelle('a_faire')}>Créer la première tâche</button>
        </div>
      ) : (
        <Kanban
          colonnes={STATUTS_TACHE}
          elements={tachesFiltrees}
          rendreCarte={(t) => <CarteTache t={t} />}
          onOuvrir={(t) => ouvrirTache(t.id)}
          onDeplacer={deplacer}
          videTexte={filtreAssigne || filtrePriorite ? 'Rien pour ce filtre' : 'Aucune tâche'}
        />
      )}

      {tacheOuverte && (
        <FicheTache
          tacheId={tacheOuverte}
          moi={moi}
          utilisateurs={ref.utilisateurs}
          onFermer={() => ouvrirTache(null)}
          onModifie={charger}
        />
      )}
      {nouvelle && ref.pret && (
        <ModaleNouvelleTache
          projetId={p.id}
          statut={nouvelle}
          utilisateurs={ref.utilisateurs}
          creer={apiTaches.creer}
          onFermer={() => setNouvelle(null)}
          onCree={(tid) => { setNouvelle(null); charger(); ouvrirTache(tid); }}
        />
      )}
      {suppression && (
        <ModaleSuppression d={d} moi={moi} onFermer={() => setSuppression(false)}
          onArchiver={archiverProjet} onSupprimer={supprimerProjet} />
      )}
      {edition && ref.pret && (
        <ModaleProjet
          projet={p}
          membresInitiaux={membres}
          moi={moi}
          utilisateurs={ref.utilisateurs}
          entreprises={ref.entreprises}
          onFermer={() => setEdition(false)}
          enregistrer={async ({ membres: m, ...v }) => { await apiProjets.modifier(p.id, v); await apiProjets.membres(p.id, m); }}
          onEnregistre={() => { setEdition(false); charger(); }}
        />
      )}
    </div>
  );
}
